/**
 * useFiatPrices Hook (P5g)
 *
 * Polls USDC → fiat rates from the backend every 60s.
 * Returns a formatting function that converts USDC amounts to fiat display strings.
 *
 * When currencyCode is 'usd', no conversion is needed (USDC ~ USD) — rate is null
 * and formatFiatAmount returns null so callers fall back to existing $ display.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { pricesApi } from '@/services/api'

export interface UseFiatPricesResult {
  rate: number | null
  stale: boolean
  formatFiatAmount: (usdcAmount: string | number) => string | null
}

export function useFiatPrices(currencyCode: string): UseFiatPricesResult {
  const [rates, setRates] = useState<Record<string, number>>({})
  const [stale, setStale] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // USD = no conversion needed (USDC ~ USD)
  const isUsd = currencyCode === 'usd'
  const rate = isUsd ? null : (rates[currencyCode] ?? null)

  const fetchRates = useCallback(async () => {
    if (isUsd) return
    try {
      const result = await pricesApi.getUsdcRates()
      if (result) {
        setRates(result.rates)
        setStale(result.stale)
      }
    } catch {
      // Never block UI on price fetch failure
    }
  }, [isUsd])

  useEffect(() => {
    fetchRates()
    intervalRef.current = setInterval(fetchRates, 60_000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchRates])

  const formatFiatAmount = useCallback(
    (usdcAmount: string | number): string | null => {
      if (rate == null) return null
      const num = typeof usdcAmount === 'string' ? parseFloat(usdcAmount) : usdcAmount
      if (!isFinite(num) || num === 0) return null
      const converted = num * rate
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode.toUpperCase(),
      }).format(converted)
      const prefix = stale ? '~' : ''
      return `${prefix}${formatted}`
    },
    [rate, currencyCode, stale],
  )

  return { rate, stale, formatFiatAmount }
}
